import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { ApolloQueryResult } from 'apollo-client';
import { useHistory } from 'react-router-dom';

import { CustomButton } from '../../components/UI/CustomButton';
import {
  LearnerInfo,
  Tenant,
  Query,
  GetTeamInviteInfoArgs,
  Mutation,
  JoinTeamByInviteHashArgs,
} from '../../types';
import FullScreenBackgroundWrap from '../../components/UI/FullScreenBackgroudWrap';
import { translate } from '../../utils/helpers';
import useStyles from './styles';
import { Loader } from '../../components/Loader';
import { useAuth } from '../../utils/hooks';
import { AuthMode } from '../../context/AuthProvider/AuthContext';
import { GET_INVITE_INFO_BY_HASH, JOIN_TEAM_BY_HASH } from '../../graphql/user';
import { JOIN_TEAM_LINK } from '../../routes';

interface InvitePageProps {
  tenant?: Tenant | null;
  userInfo?: LearnerInfo;
  userLoading: boolean;
  refetchUserInfo: (
    variables?: Record<string, any> | undefined,
  ) => Promise<ApolloQueryResult<Pick<Query, 'getLearnerInfo'>>>;
  setIsAfterAuthorization: React.Dispatch<React.SetStateAction<boolean>>;
}

const InvitePage: FC<InvitePageProps> = ({
  tenant,
  userInfo,
  userLoading,
  refetchUserInfo,
  setIsAfterAuthorization,
}) => {
  const classes = useStyles();

  const params = useParams();
  const hash = params?.hash;

  const history = useHistory();

  const [loadingToken, setLoadingToken] = useState();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth();

  const { loginWithRedirect } = useAuth();

  const { data: inviteData, loading: loadingInviteData } = useQuery<
    Pick<Query, 'getInviteInfoByHash'>,
    GetTeamInviteInfoArgs
  >(GET_INVITE_INFO_BY_HASH, {
    variables: {
      hash: hash || '',
    },
    skip: !hash,
  });

  const inviteInfo = inviteData?.getInviteInfoByHash;

  const [joinTeam, { loading: loadingInvitation }] = useMutation<
    Pick<Mutation, 'joinTeamByInviteHash'>,
    JoinTeamByInviteHashArgs
  >(JOIN_TEAM_BY_HASH, {
    variables: {
      hash: hash,
    },
    onCompleted: async () => {
      setLoadingToken(true);
      try {
        await getAccessTokenSilently({ ignoreCache: true });
        await refetchUserInfo();
        setIsAfterAuthorization(true);
        history.replace('/my-account');
      } catch (e) {
        try {
          await getAccessTokenWithPopup({ ignoreCache: true });
          await refetchUserInfo();
          setIsAfterAuthorization(true);
          history.replace('/my-account');
        } catch (e) {}
      }
    },
  });

  const login = (mode?: AuthMode) => {
    loginWithRedirect({
      redirectUri: `${JOIN_TEAM_LINK}${hash}`,
      mode: mode,
    });
  };

  if (userLoading || loadingInviteData || loadingInvitation || loadingToken) {
    return (
      <>
        <Helmet>
          <title>Invitation {tenant?.title ? `- ${tenant.title}` : ''}</title>
        </Helmet>
        <Loader />
      </>
    );
  }

  if (!loadingInviteData && !inviteInfo) {
    //TODO add redirect to 404 page
  }

  return (
    <>
      <Helmet>
        <title>Invitation {tenant?.title ? `- ${tenant.title}` : ''}</title>
      </Helmet>
      <FullScreenBackgroundWrap logoUrl={tenant?.logo_url}>
        <div className={classes.wrapper}>
          <Typography variant="h2" className={classes.title}>
            {translate('page.invite.join')} {inviteInfo?.company?.name}
          </Typography>
          <Typography variant="h3" className={classes.description}>
            {translate('page.invite.joinText', {
              teamName: inviteInfo?.team_name,
              organizationName: inviteInfo?.company?.name,
              tenantTitle: tenant?.title,
            })}
          </Typography>
          {!inviteInfo?.seats_available && (
            <Typography variant="h3" className={classes.description}>
              {translate('page.invite.noAvailableSeats')}
            </Typography>
          )}
          {!!inviteInfo?.seats_available && !userInfo && (
            <div className={classes.buttonsWrapper}>
              <CustomButton onClick={() => login('signUp')}>
                {translate('button.signUp')}
              </CustomButton>
              <CustomButton btnClass={classes.btn} onClick={() => login()}>
                {translate('button.iHaveAccount')}
              </CustomButton>
            </div>
          )}
          {!!inviteInfo?.seats_available && !!userInfo && (
            <CustomButton onClick={() => joinTeam()} btnClass={classes.joinBtn}>
              {translate('button.joinTeam')}
            </CustomButton>
          )}
        </div>
      </FullScreenBackgroundWrap>
    </>
  );
};

export default InvitePage;
